import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import AppInput from '../Inputs/AppInput';
import { apiGetProjects } from '../../api/projects';
import { apiGetFolders } from '../../api/folders';
import { searchByName } from '../../helpers/searchHelpers';
import { moveItemsToProject } from '../../helpers/projectHelpers';
import { debug } from '../../helpers/debug';
import Icon from '../Icon';

export default function ModalMoveItems({
  isOpen,
  onCancel,
  item,
  user,
  srcProjectId,
  itemsMap,
  setItemsMap,
  tabs,
  setTabs,
  setActiveTabId,
  refresh
}) {
  const [projects, setProjects] = useState([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [folders, setFolders] = useState([]);
  const [folderQuery, setFolderQuery] = useState('');
  const [folderResults, setFolderResults] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [selectedFolderLabel, setSelectedFolderLabel] = useState('');
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setQuery('');
    setResults([]);
    setSelectedProject(null);
    setFolderQuery('');
    setFolderResults([]);
    setSelectedFolderLabel('');
    setSelectedFolderId(null);
    setFolders([]);

    let mounted = true;
    (async () => {
      try {
        if (!user?.id) return;
        const list = await apiGetProjects(user.id);
        if (!mounted) return;
        const writable = (list || []).filter(p => ['owner', 'editor'].includes(String(p?.access_level || '').toLowerCase()));
        setProjects(writable || []);
      } catch (err) {
        setProjects([]);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [isOpen, user]);

  useEffect(() => {
    if (!query) {
      setResults([]);
      return;
    }
    setResults(searchByName(projects, query, { key: 'name' }) || []);
  }, [query, projects]);

  useEffect(() => {
    if (!selectedProject) return;

    let mounted = true;
    (async () => {
      try {
        const f = await apiGetFolders(selectedProject.id);
        if (!mounted) return;
        setFolders(Array.isArray(f) ? f : []);
      } catch (err) {
        setFolders([]);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [selectedProject]);

  useEffect(() => {
    if (!folderQuery) {
      setFolderResults([]);
      return;
    }
    setFolderResults(searchByName(folders, folderQuery, { key: 'name' }) || []);
  }, [folderQuery, folders]);

  const handleSelectProject = (project) => {
    setSelectedProject(project);
    setSelectedFolderId(null);
    setSelectedFolderLabel('');
    setFolderQuery('');
    setFolderResults([]);
    setResults([]);
    setQuery('');
  };

  const handleSelectFolder = (folder) => {
    const folderId = folder?.id ?? null;
    setSelectedFolderId(folderId);
    setSelectedFolderLabel(folder?.name || 'Untitled Folder');
    setFolderQuery('');
    setFolderResults([]);
  };

  const handleConfirm = async () => {
    if (!selectedProject || !item) return;

    debug('ModalMoveItems.handleConfirm', { type: 'audit', level: 1 }, {
      itemType: item?.type,
      itemId: item?.id,
      srcProjectId,
      destProjectId: selectedProject.id,
      destParentId: selectedFolderId ?? null
    });

    setMoving(true);
    try {
      const result = await moveItemsToProject({
        items: [item],
        srcProjectId,
        destProjectId: selectedProject.id,
        destParentId: selectedFolderId ?? null,
        preferRpc: true,
        allowClientFallback: false,
        userId: user?.id,
        itemsMap,
        setItemsMap,
        tabs,
        setTabs,
        setActiveTabId,
        refresh
      });

      debug('ModalMoveItems.handleConfirm.success', { type: 'audit', level: 1 }, {
        movedCount: Array.isArray(result?.moved) ? result.moved.length : 0,
        skippedCount: Array.isArray(result?.skipped) ? result.skipped.length : 0,
        result
      });

      onCancel?.();
    } catch (err) {
      // Keep modal open on failure so user can retry/change destination.
      // Error toast is emitted by moveItemsToProject.
      debug('ModalMoveItems.handleConfirm.error', { type: 'error' }, err);
    } finally {
      setMoving(false);
    }
  };

  const isSameProjectSelected = selectedProject && String(selectedProject.id) === String(srcProjectId);

  return (
    <Modal
      isOpen={Boolean(isOpen)}
      title={item ? `Move "${item.name || 'Untitled'}" to project` : 'Move to project'}
      onCancel={() => {
        if (!moving) onCancel?.();
      }}
      onConfirm={handleConfirm}
      confirmText="Move to Project"
      confirmDisabled={moving || !selectedProject || isSameProjectSelected}
      cancelText="Cancel"
    >
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', marginBottom: 6 }}>Search projects you can write to</label>
        {selectedProject ? (
          <div className="contextItem linkProjectSelection">
            <span className="linkProjectSelectionLabel">{selectedProject.name || 'Untitled Project'}</span>
            <button
              type="button"
              className="iconButton small"
              aria-label="Clear selected project"
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedProject(null);
                setSelectedFolderId(null);
                setSelectedFolderLabel('');
                setFolders([]);
                setFolderQuery('');
                setFolderResults([]);
                setQuery('');
                setResults([]);
              }}
            >
              <Icon icon={'x'} className="icon small x" />
            </button>
          </div>
        ) : (
          <>
            <AppInput
              type="text"
              className="linkInput linkProjectSearchInput"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search projects"
              autoComplete="off"
              stopPropagation
            />
            <div className="linkProjectResults">
              {query.trim().length === 0 ? null : results.length === 0 ? (
                <div className="hint">No projects found.</div>
              ) : (
                results.map(projectResult => (
                  <div
                    key={`proj-${projectResult.id}`}
                    className="searchResultItem linkProjectResultItem"
                    role="button"
                    tabIndex={0}
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelectProject(projectResult);
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter' && e.key !== ' ') return;
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelectProject(projectResult);
                    }}
                  >
                    <span>{projectResult.name || 'Untitled Project'}</span>
                    <span className="chip">{String(projectResult.access_level || 'viewer')}</span>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {selectedProject && (
        <div style={{ marginTop: 10 }}>
          {selectedFolderId ? (
            <div className="contextItem linkProjectSelection">
              <span className="linkProjectSelectionLabel">{selectedFolderLabel || 'Untitled Folder'}</span>
              <button
                type="button"
                className="iconButton small"
                aria-label="Clear selected folder"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedFolderId(null);
                  setSelectedFolderLabel('');
                  setFolderQuery('');
                  setFolderResults([]);
                }}
              >
                <Icon icon={'x'} className="icon small x" />
              </button>
            </div>
          ) : (
            <>
              <AppInput
                type="text"
                className="linkInput linkProjectSearchInput"
                value={folderQuery}
                onChange={e => setFolderQuery(e.target.value)}
                placeholder="Select a folder (optional)"
                autoComplete="off"
                stopPropagation
              />
              <div className="linkProjectResults">
                {folderQuery.trim().length === 0 ? null : folderResults.length === 0 ? (
                  <div className="hint">No folders found.</div>
                ) : (
                  folderResults.map(folderResult => (
                    <div
                      key={`folder-${folderResult.id}`}
                      className="searchResultItem linkProjectResultItem"
                      role="button"
                      tabIndex={0}
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectFolder(folderResult);
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectFolder(folderResult);
                      }}
                    >
                      <span>{folderResult.name || 'Untitled Folder'}</span>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
